import { msg, type Message } from '../i18n/t.ts';

/** §5: the node is polled every 2 s (§9 allows 1–3). */
export const POLL_MS = 2_000;
/** §10.8: past this, a note says the tutor is busy and the feedback will come. */
export const BUSY_NOTE_MS = 30_000;
/**
 * §10.8: longer than the server's own 10-minute wait for a live
 * conversation and the guard, so a retelling the server will still finish is
 * not abandoned by the page first. The server's bound is 10 minutes of
 * waiting plus the run itself, so a long run can still outlast this, and
 * `late` then points to the progress page.
 */
export const MAX_WAIT_MS = 11 * 60_000;

export type Status = 'PENDING' | 'READY' | 'FAILED';

/**
 * What the feedback screen does with a retelling `elapsed` ms after it began
 * waiting. Past the bound it stops polling but offers no retry: retry runs
 * only on a row the server has marked FAILED (§10.8), and a PENDING one
 * would answer CONFLICT. `late` says so and points to the history instead.
 */
export function waiting(status: Status, elapsedMs: number): { poll: boolean; busy: boolean; late: boolean } {
  if (status !== 'PENDING') return { poll: false, busy: false, late: false };
  if (elapsedMs >= MAX_WAIT_MS) return { poll: false, busy: false, late: true };
  return { poll: true, busy: elapsedMs >= BUSY_NOTE_MS, late: false };
}

export type Span = { readonly spanStart: number | null | undefined; readonly spanEnd: number | null | undefined };
export type Piece = { text: string; mark: number | null };

/**
 * The learner's text cut into plain and underlined pieces, `mark` being the
 * correction's index. Spans are the server's character offsets
 * (`analysis::locate_forms` counts `chars()`), so the text is walked by code
 * point, not by UTF-16 unit. A correction found only by its words (no span),
 * one out of range, empty, or overlapping one already placed is listed but
 * not underlined.
 */
export function underline(text: string, corrections: readonly Span[]): Piece[] {
  const chars = Array.from(text);
  const placed = corrections
    .map((c, i) => ({ start: c.spanStart, end: c.spanEnd, i }))
    .filter(
      (c): c is { start: number; end: number; i: number } =>
        typeof c.start === 'number' &&
        typeof c.end === 'number' &&
        c.start >= 0 &&
        c.start < c.end &&
        c.end <= chars.length,
    )
    .sort((a, b) => a.start - b.start || a.i - b.i);
  const pieces: Piece[] = [];
  let at = 0;
  for (const c of placed) {
    if (c.start < at) continue;
    if (c.start > at) pieces.push({ text: chars.slice(at, c.start).join(''), mark: null });
    pieces.push({ text: chars.slice(c.start, c.end).join(''), mark: c.i });
    at = c.end;
  }
  if (at < chars.length) pieces.push({ text: chars.slice(at).join(''), mark: null });
  return pieces;
}

/**
 * `submitRetelling`'s refusals. `BAD_USER_INPUT` is a body the client's own
 * gates should have kept back (too short, too long, a sentence twice), so it
 * says only that this one was not taken; `NOT_FOUND` is a passage no longer
 * offered.
 */
export function submitCopy(code: string | null): Message {
  switch (code) {
    case 'RATE_LIMITED':
      return msg('listen-rate-limited');
    case 'NOT_FOUND':
      return msg('listen-gone');
    case 'BAD_USER_INPUT':
      return msg('listen-submit-refused');
    default:
      return msg('listen-submit-failed');
  }
}

/** `retryRetelling`'s; its limit is the submit's (§10.8: "retries included"). */
export function retryCopy(code: string | null): Message {
  return code === 'RATE_LIMITED' ? msg('listen-rate-limited') : msg('listen-retry-failed');
}

/**
 * A sentence's `speak` refused. Not `aidErrors.copyFor`: its
 * `BAD_USER_INPUT` is about a native language, which this call never reads.
 */
export function soundCopy(code: string | null): Message {
  return code === 'RATE_LIMITED' ? msg('aid-rate-limited') : msg('aid-failed');
}
